// Geometry for Otto's resizable chat window. Pure math, no DOM — the widget owns the state and the
// pointer events, this owns "what size should the panel be".
//
// Two things make the arithmetic non-obvious, and both live here so the component stays readable:
//
//  1. The panel is anchored BOTTOM-RIGHT (`.fai-panel { right: 0; bottom: 56px }`) and moved with a
//     `translate` offset. So growing it extends LEFT and UP for free, but dragging the east/south edge
//     has to grow the size AND translate the panel by the same delta to keep the opposite edge still.
//  2. The stored width is the CHAT column's width, NOT the panel's. The history sidebar is a fixed-width
//     column beside the chat, so `panelWidth()` adds it back when history is open — opening history
//     widens the window instead of squeezing the conversation into half of it.

// Defaults — these mirror `.fai-panel` in filterAssistant.css (the CSS values are the pre-JS fallback).
export const PANEL_BASE_W = 380;
export const PANEL_BASE_H = 600;

// Width of the history sidebar. MUST match `.fai-history { flex: 0 0 240px }` in filterAssistant.css;
// panelSize.test.js reads the stylesheet and fails if the two drift apart.
export const HISTORY_W = 240;

// Floor: below this the composer and the proposal cards stop being usable.
export const PANEL_MIN_W = 300;
export const PANEL_MIN_H = 280;

// The one-click "enlarge" preset: markedly more room in both axes than the default, clamped to the
// viewport by `clampSize` at the moment it is applied.
export const PANEL_LARGE_W = 680;
export const PANEL_LARGE_H = 820;

// Viewport gutters. These mirror `.fai-panel`'s `max-width: calc(100vw - 40px)` /
// `max-height: calc(100vh - 120px)`, which stay in the CSS as the hard backstop for a viewport that
// shrinks without re-rendering us.
const VIEWPORT_W_GUTTER = 40;
const VIEWPORT_H_GUTTER = 120;

// Keep-on-screen margin for a drag, matching the header drag's clamp.
const EDGE_MARGIN = 8;

const clamp = (n, lo, hi) => Math.min(Math.max(n, lo), Math.max(lo, hi));

// Viewport size, defensively — `window` can be absent (tests/SSR) and innerWidth can be 0 in a detached
// frame, either of which would otherwise clamp the panel down to its minimum.
export const readViewport = vp => {
    if (vp && vp.w && vp.h) return vp;
    const w = typeof window !== "undefined" && window.innerWidth ? window.innerWidth : 1280;
    const h = typeof window !== "undefined" && window.innerHeight ? window.innerHeight : 800;
    return { w, h };
};

// The bounds a chat width / panel height must stay inside. `historyOpen` matters because the sidebar
// eats into the same viewport budget the chat column is measured against.
export const sizeLimits = (historyOpen, vp) => {
    const { w, h } = readViewport(vp);
    const sidebar = historyOpen ? HISTORY_W : 0;
    return {
        minW: PANEL_MIN_W,
        minH: PANEL_MIN_H,
        maxW: Math.max(PANEL_MIN_W, w - VIEWPORT_W_GUTTER - sidebar),
        maxH: Math.max(PANEL_MIN_H, h - VIEWPORT_H_GUTTER)
    };
};

export const clampSize = (size, historyOpen, vp) => {
    const lim = sizeLimits(historyOpen, vp);
    return { w: clamp(size.w, lim.minW, lim.maxW), h: clamp(size.h, lim.minH, lim.maxH) };
};

// Chat width -> rendered panel width. The clamp is applied here rather than to the stored size so that
// closing history (or moving to a bigger screen) hands the chat column its full width back, instead of
// permanently shrinking what the user asked for.
export const panelWidth = (chatW, historyOpen, vp) =>
    Math.min(chatW + (historyOpen ? HISTORY_W : 0), readViewport(vp).w - VIEWPORT_W_GUTTER);

export const panelHeight = (h, vp) => Math.min(h, readViewport(vp).h - VIEWPORT_H_GUTTER);

// The enlarge preset, cut down to whatever the current viewport can actually show.
export const enlargedSize = (historyOpen, vp) => clampSize({ w: PANEL_LARGE_W, h: PANEL_LARGE_H }, historyOpen, vp);

export const defaultSize = () => ({ w: PANEL_BASE_W, h: PANEL_BASE_H });

// Is the window already at (or past) the enlarge preset? Derived rather than stored as a flag, so a
// manual edge-drag flips the header button back to "Enlarge" on its own, and so the button still reads
// "Restore" after enlarging on a short screen where the preset got clamped.
export const isEnlarged = (size, historyOpen, vp) => {
    const target = enlargedSize(historyOpen, vp);
    return size.w >= target.w && size.h >= target.h;
};

// One frame of an edge/corner drag.
//
// `base` is the snapshot taken on pointer-down: { w, h, offX, offY, rect, historyOpen } where w/h are the
// chat width + panel height at that moment, offX/offY the panel's translate, and `rect` its bounding box.
// `dir` is a compass string ("n", "se", ...). Returns the new size AND translate offset.
export const resizeFrom = (base, dir, dxRaw, dyRaw, vp) => {
    const view = readViewport(vp);
    const rect = base.rect;
    let dx = dxRaw;
    let dy = dyRaw;

    // Stop the dragged edge at the viewport, so no resize can shove the panel out of reach. West/north
    // move the edge that grows the panel; east/south move the panel itself (see the offset below).
    if (rect) {
        if (dir.indexOf("w") !== -1) dx = Math.max(dx, EDGE_MARGIN - rect.left);
        if (dir.indexOf("e") !== -1) dx = Math.min(dx, view.w - EDGE_MARGIN - rect.right);
        if (dir.indexOf("n") !== -1) dy = Math.max(dy, EDGE_MARGIN - rect.top);
        if (dir.indexOf("s") !== -1) dy = Math.min(dy, view.h - EDGE_MARGIN - rect.bottom);
    }

    const lim = sizeLimits(base.historyOpen, view);
    let { w, h, offX, offY } = base;

    if (dir.indexOf("w") !== -1) w = clamp(base.w - dx, lim.minW, lim.maxW);
    if (dir.indexOf("e") !== -1) {
        w = clamp(base.w + dx, lim.minW, lim.maxW);
        // Right edge follows the pointer: translate by the delta the clamp actually allowed, not by dx,
        // or the panel keeps sliding right after the width has stopped growing.
        offX = base.offX + (w - base.w);
    }
    if (dir.indexOf("n") !== -1) h = clamp(base.h - dy, lim.minH, lim.maxH);
    if (dir.indexOf("s") !== -1) {
        h = clamp(base.h + dy, lim.minH, lim.maxH);
        offY = base.offY + (h - base.h);
    }

    return { w, h, offX, offY };
};

// Every edge and corner of the window, with the cursor each one shows. The order is the DOM order of the
// handles; corners come last so they sit above the edge strips they overlap.
export const RESIZE_HANDLES = [
    { dir: "n", label: "Resize from the top edge" },
    { dir: "s", label: "Resize from the bottom edge" },
    { dir: "w", label: "Resize from the left edge" },
    { dir: "e", label: "Resize from the right edge" },
    { dir: "nw", label: "Resize from the top-left corner" },
    { dir: "ne", label: "Resize from the top-right corner" },
    { dir: "sw", label: "Resize from the bottom-left corner" },
    { dir: "se", label: "Resize from the bottom-right corner" }
];
